import type { AssistantMessage, Usage } from "@bastani/pi-ai/compat";
import { CONFIG_DIR_NAME } from "../../config.js";
import { collectCacheMisses, createCacheMissModelSource, describeCacheMissCause } from "../../core/cache-stats.ts";
import { markLifecycleTiming } from "../../core/lifecycle-timings.ts";
import { VERBATIM_COMPACTION_PREFIX } from "../../core/messages.ts";
import { serviceTierWarnings } from "../../core/service-tier-warnings.ts";
import type { CustomEntry } from "../../core/session-manager.ts";
import { buildContextEntries, type SessionEntry, sessionEntryToContextMessages } from "../../core/session-manager.ts";
import { yieldToEventLoop } from "../../utils/event-loop.ts";
import { IsolatedInteractiveRuntime } from "../interactive-engine/isolated-runtime.js";
import { RemoteCustomMessageComponent, RemoteToolExecutionComponent } from "../interactive-engine/remote-renderer.ts";
import { appendBoundedStderr } from "../rpc/rpc-client-process.js";
import { CustomEntryComponent } from "./components/custom-entry.ts";
import { createMermaidMarkdownTransformer } from "./components/mermaid.ts";
import { ThemedText } from "./components/themed-text.js";
import { InteractiveModeBase } from "./interactive-mode-base.ts";
import {
	type AgentMessage,
	APP_NAME,
	AssistantMessageComponent,
	addChatTranscriptEntry,
	BashExecutionComponent,
	BranchSummaryMessageComponent,
	type ChatMessageEntry,
	type ChatMessageRenderOptions,
	CompactionBoundaryMessageComponent,
	type Component,
	CustomMessageComponent,
	chatEntriesFromAgentMessages,
	hasTrustRequiringProjectResources,
	type PersistedMessageMeta,
	parseSkillBlock,
	recordTimeSinceReset,
	renderChatMessageEntry,
	type SessionContext,
	SkillInvocationMessageComponent,
	Spacer,
	Text,
	ToolExecutionComponent,
	type TruncationResult,
	theme,
	UserMessageComponent,
	type VerbatimCompactionResult,
} from "./interactive-mode-deps.ts";
import type { InteractiveSubmission } from "./interactive-submission.ts";
import { refreshInteractiveTasks } from "./interactive-task-projection.js";

function countDroppedThinkingBlocks(message: AssistantMessage): number {
	return (message.diagnostics ?? []).reduce((count, diagnostic) => {
		if (diagnostic.type !== "anthropic_input_transformations") return count;
		const dropped = diagnostic.details?.droppedBlockCount;
		return count + (typeof dropped === "number" && dropped > 0 ? dropped : 0);
	}, 0);
}

function addServiceTierWarnings(mode: InteractiveModeBase, message: AssistantMessage): void {
	for (const warning of serviceTierWarnings(message)) {
		mode.chatContainer.addChild(new Spacer(1));
		mode.chatContainer.addChild(new Text(theme.fg("warning", `Warning: ${warning}`), 1, 0));
	}
}

InteractiveModeBase.prototype.maybeShowAssistantDiagnostics = function (
	this: InteractiveModeBase,
	message: AssistantMessage,
): void {
	addServiceTierWarnings(this, message);
	if (!this.settingsManager.getShowCacheMissNotices()) return;

	const count = countDroppedThinkingBlocks(message);
	if (count === 0) return;
	let previousCount = 0;
	// The live message_end notification precedes persistence of this response.
	const branch = this.sessionManager.getBranch();
	for (let i = branch.length - 1; i >= 0; i--) {
		const entry = branch[i];
		if (entry.type === "message" && entry.message.role === "assistant") {
			previousCount = countDroppedThinkingBlocks(entry.message);
			break;
		}
	}
	if (count <= previousCount) return;
	const noun = count === 1 ? "thinking block" : "thinking blocks";
	this.chatContainer.addChild(new Spacer(1));
	this.chatContainer.addChild(
		new Text(theme.fg("warning", `Anthropic dropped ${count} ${noun} (details in session)`), 1, 0),
	);
};

// Weak keys leave cleared/rebuilt transcripts collectible without separate lifecycle state.
// Only persistent diagnostic statuses participate; ordinary chat/status is never evicted.
const diagnosticStatuses = new WeakMap<Component, { spacer: Spacer; bytes: number }>();
const MAX_DIAGNOSTIC_STATUSES = 64;
const MAX_DIAGNOSTIC_HISTORY_BYTES = 256 * 1024;

InteractiveModeBase.prototype.showStatus = function (
	this: InteractiveModeBase,
	message: string,
	persist = false,
): void {
	const children = this.chatContainer.children;
	const last = children.length > 0 ? children[children.length - 1] : undefined;
	const secondLast = children.length > 1 ? children[children.length - 2] : undefined;

	if (!persist && last && secondLast && last === this.lastStatusText && secondLast === this.lastStatusSpacer) {
		this.lastStatusText.setText(theme.fg("dim", message));
		this.ui.requestRender();
		return;
	}

	if (persist) message = appendBoundedStderr("", message);
	const spacer = new Spacer(1);
	const text = new Text(theme.fg("dim", message), 1, 0);
	this.chatContainer.addChild(spacer);
	this.chatContainer.addChild(text);
	this.lastStatusSpacer = persist ? undefined : spacer;
	this.lastStatusText = persist ? undefined : text;
	if (persist) {
		diagnosticStatuses.set(text, { spacer, bytes: Buffer.byteLength(message) });
		let count = 0;
		let bytes = 0;
		for (const child of [...children].reverse()) {
			const diagnostic = diagnosticStatuses.get(child);
			if (!diagnostic) continue;
			count++;
			bytes += diagnostic.bytes;
			if (count > MAX_DIAGNOSTIC_STATUSES || bytes > MAX_DIAGNOSTIC_HISTORY_BYTES) {
				this.chatContainer.removeChild(child);
				this.chatContainer.removeChild(diagnostic.spacer);
			}
		}
	}
	this.ui.requestRender();
};

InteractiveModeBase.prototype.renderDeferredUserInput = function (this: InteractiveModeBase, text: string): void {
	this.deferredRenderedUserInputs.push(text);
	const startIndex = this.chatContainer.children.length;
	this.addMessageToChat({ role: "user", content: text } as AgentMessage);
	const renderedComponents = this.chatContainer.children.slice(startIndex);
	const trackedComponents = this.deferredRenderedUserInputComponents.get(text) ?? [];
	trackedComponents.push(renderedComponents);
	this.deferredRenderedUserInputComponents.set(text, trackedComponents);
	this.updatePendingMessagesDisplay();
	this.ui.requestRender();
};

InteractiveModeBase.prototype.consumeDeferredRenderedUserInput = function (
	this: InteractiveModeBase,
	text: string,
): boolean {
	const index = this.deferredRenderedUserInputs.indexOf(text);
	if (index === -1) return false;
	this.deferredRenderedUserInputs.splice(index, 1);
	const trackedComponents = this.deferredRenderedUserInputComponents.get(text);
	trackedComponents?.shift();
	if (trackedComponents && trackedComponents.length === 0) {
		this.deferredRenderedUserInputComponents.delete(text);
	}
	return true;
};

InteractiveModeBase.prototype.discardDeferredRenderedUserInput = function (
	this: InteractiveModeBase,
	text: string,
): void {
	const index = this.deferredRenderedUserInputs.indexOf(text);
	if (index !== -1) this.deferredRenderedUserInputs.splice(index, 1);
	const trackedComponents = this.deferredRenderedUserInputComponents.get(text);
	const componentsToRemove = trackedComponents?.shift();
	if (trackedComponents && trackedComponents.length === 0) {
		this.deferredRenderedUserInputComponents.delete(text);
	}
	if (!componentsToRemove) return;
	for (const component of componentsToRemove) {
		this.chatContainer.removeChild(component);
	}
	this.updatePendingMessagesDisplay();
	this.ui.requestRender();
};

InteractiveModeBase.prototype.getMarkdownTransformers = function (this: InteractiveModeBase) {
	const mode = this.settingsManager.getMermaidRenderingMode();
	if (this.mermaidMarkdownTransformerMode !== mode) {
		this.mermaidMarkdownTransformerMode = mode;
		this.mermaidMarkdownTransformer = createMermaidMarkdownTransformer({
			getMode: () => this.settingsManager.getMermaidRenderingMode(),
			theme,
		});
	}
	const extensionTransformers =
		this.runtimeHost instanceof IsolatedInteractiveRuntime
			? []
			: this.session.extensionRunner.getMarkdownTransformers();
	return [this.mermaidMarkdownTransformer, ...extensionTransformers];
};

InteractiveModeBase.prototype.chatMessageRenderOptions = function (
	this: InteractiveModeBase,
): ChatMessageRenderOptions {
	const isolated = this.runtimeHost instanceof IsolatedInteractiveRuntime;
	return {
		ui: this.ui,
		cwd: this.sessionManager.getCwd(),
		markdownTheme: this.getMarkdownThemeWithSettings(),
		hideThinkingBlock: this.hideThinkingBlock,
		hiddenThinkingLabel: this.hiddenThinkingLabel,
		toolOutputExpanded: this.toolOutputExpanded,
		showImages: this.settingsManager.getShowImages(),
		imageWidthCells: this.settingsManager.getImageWidthCells(),
		outputPad: this.outputPad,
		renderLatex: this.settingsManager.getLatexRenderingEnabled(),
		markdownTransformers: this.getMarkdownTransformers(),
		getToolDefinition: isolated ? undefined : (toolName) => this.getRegisteredToolDefinition(toolName),
		getCustomMessageRenderer: isolated
			? undefined
			: (customType) => this.session.extensionRunner.getMessageRenderer(customType),
		createToolComponent: isolated
			? (entry) => {
					const component = new RemoteToolExecutionComponent(
						entry.toolName,
						entry.toolCallId,
						entry.args,
						{
							showImages: this.settingsManager.getShowImages(),
							imageWidthCells: this.settingsManager.getImageWidthCells(),
							outputPad: this.outputPad,
						},
						this.runtimeHost as IsolatedInteractiveRuntime,
						() => this.ui.requestRender(),
					);
					component.setExpanded(this.toolOutputExpanded);
					if (entry.result) component.updateResult(entry.result, entry.isPartial ?? false);
					return component;
				}
			: undefined,
		createCustomMessageComponent: isolated
			? (message) => {
					const component = new RemoteCustomMessageComponent(
						message,
						this.runtimeHost as IsolatedInteractiveRuntime,
						() => this.ui.requestRender(),
						this.outputPad,
					);
					component.setExpanded(this.toolOutputExpanded);
					return component;
				}
			: undefined,
	};
};

InteractiveModeBase.prototype.addRenderedChatEntry = function (
	this: InteractiveModeBase,
	entry: ChatMessageEntry,
): Component {
	const component = renderChatMessageEntry(entry, this.chatMessageRenderOptions());
	addChatTranscriptEntry(this.chatContainer, component, entry.role);
	return component;
};

InteractiveModeBase.prototype.addCompactionBoundaryToChat = function (
	this: InteractiveModeBase,
	result: VerbatimCompactionResult,
): void {
	this.chatContainer.addChild(new Spacer(1));
	const component = new CompactionBoundaryMessageComponent(result, this.outputPad);
	component.setExpanded(this.toolOutputExpanded);
	this.chatContainer.addChild(component);
};

InteractiveModeBase.prototype.addCompactionCostNotice = function (
	this: InteractiveModeBase,
	kind: "compaction" | "branch_summary",
	usage: Usage,
): void {
	if (!this.settingsManager.getShowCacheMissNotices()) return;
	const tokens = usage.input + usage.output + usage.cacheRead + usage.cacheWrite;
	const cost = usage.cost.total >= 0.01 ? ` (~$${usage.cost.total.toFixed(2)})` : "";
	const label = kind === "compaction" ? "Compaction" : "Branch summary";
	this.chatContainer.addChild(new Spacer(1));
	this.chatContainer.addChild(
		new Text(theme.fg("warning", `${label}: ${tokens.toLocaleString()} tokens billed${cost}`), 1, 0),
	);
};

InteractiveModeBase.prototype.addCustomEntryToChat = function (this: InteractiveModeBase, entry: CustomEntry): void {
	const renderer = this.session.extensionRunner.getEntryRenderer(entry.customType);
	if (!renderer) return;
	const runner = this.session.extensionRunner;
	const component = new CustomEntryComponent(
		entry,
		(...args) => runner.renderEntry(entry.customType, ...args),
		this.outputPad,
	);
	component.setExpanded(this.toolOutputExpanded);
	if (!component.hasContent()) return;
	const streamingIndex = this.streamingComponent ? this.chatContainer.children.indexOf(this.streamingComponent) : -1;
	if (streamingIndex >= 0) this.chatContainer.children.splice(streamingIndex, 0, component);
	else this.chatContainer.addChild(component);
};

InteractiveModeBase.prototype.addMessageToChat = function (
	this: InteractiveModeBase,
	message: AgentMessage,
	options?: { populateHistory?: boolean },
): void {
	const markdownTransformers = this.getMarkdownTransformers();
	switch (message.role) {
		case "bashExecution": {
			const component = new BashExecutionComponent(
				message.command,
				this.ui,
				message.excludeFromContext,
				this.outputPad,
			);
			if (message.output) {
				component.appendOutput(message.output);
			}
			component.setComplete(
				message.exitCode,
				message.cancelled,
				message.truncated ? ({ truncated: true } as TruncationResult) : undefined,
				message.fullOutputPath,
			);
			this.chatContainer.addChild(component);
			break;
		}
		case "custom": {
			if (message.display) {
				const component =
					this.runtimeHost instanceof IsolatedInteractiveRuntime
						? new RemoteCustomMessageComponent(
								message,
								this.runtimeHost,
								() => this.ui.requestRender(),
								this.outputPad,
							)
						: new CustomMessageComponent(
								message,
								this.session.extensionRunner.getMessageRenderer(message.customType),
								this.getMarkdownThemeWithSettings(),
								this.outputPad,
								this.settingsManager.getLatexRenderingEnabled(),
							);
				component.setExpanded(this.toolOutputExpanded);
				this.chatContainer.addChild(component);
			}
			break;
		}
		case "branchSummary": {
			this.chatContainer.addChild(new Spacer(1));
			const component = new BranchSummaryMessageComponent(
				message,
				this.getMarkdownThemeWithSettings(),
				this.settingsManager.getLatexRenderingEnabled(),
				this.outputPad,
			);
			component.setExpanded(this.toolOutputExpanded);
			this.chatContainer.addChild(component);
			break;
		}
		case "user": {
			const textContent = this.getUserMessageText(message);
			if (textContent) {
				if (this.chatContainer.children.length > 0) {
					this.chatContainer.addChild(new Spacer(1));
				}
				const skillBlock = parseSkillBlock(textContent);
				if (skillBlock) {
					// Render skill block (collapsible)
					const component = new SkillInvocationMessageComponent(
						skillBlock,
						this.getMarkdownThemeWithSettings(),
						this.settingsManager.getLatexRenderingEnabled(),
						this.outputPad,
					);
					component.setExpanded(this.toolOutputExpanded);
					this.chatContainer.addChild(component);
					// Render user message separately if present
					if (skillBlock.userMessage) {
						const userComponent = new UserMessageComponent(
							skillBlock.userMessage,
							this.getMarkdownThemeWithSettings(),
							this.outputPad,
							markdownTransformers,
							this.settingsManager.getLatexRenderingEnabled(),
						);
						this.chatContainer.addChild(userComponent);
					}
				} else {
					const userComponent = new UserMessageComponent(
						textContent,
						this.getMarkdownThemeWithSettings(),
						this.outputPad,
						markdownTransformers,
						this.settingsManager.getLatexRenderingEnabled(),
					);
					this.chatContainer.addChild(userComponent);
				}
				if (options?.populateHistory) {
					this.editor.addToHistory?.(textContent);
				}
			}
			break;
		}
		case "assistant": {
			const assistantComponent = new AssistantMessageComponent(
				message,
				this.hideThinkingBlock,
				this.getMarkdownThemeWithSettings(),
				this.hiddenThinkingLabel,
				this.outputPad,
				markdownTransformers,
				false,
				this.settingsManager.getLatexRenderingEnabled(),
			);
			this.chatContainer.addChild(assistantComponent);
			break;
		}
		case "toolResult": {
			// Tool results are rendered inline with tool calls, handled separately
			break;
		}
		default:
			break;
	}
};

InteractiveModeBase.prototype.renderSessionContext = function (
	this: InteractiveModeBase,
	sessionContext: SessionContext,
	options: { updateFooter?: boolean; populateHistory?: boolean } = {},
): void {
	this.pendingTools.clear();
	const pendingDeferredInputs = [...this.deferredRenderedUserInputs];
	this.deferredRenderedUserInputs = [];
	this.deferredRenderedUserInputComponents.clear();

	if (options.updateFooter) {
		this.footer.invalidate();
		this.updateEditorBorderColor();
	}

	const entries = chatEntriesFromAgentMessages(sessionContext.messages);
	for (const entry of entries) {
		const component = this.addRenderedChatEntry(entry);
		if (entry.kind === "tool" && entry.isPartial !== false && component instanceof ToolExecutionComponent) {
			this.pendingTools.set(entry.toolCallId, component);
		}
		if (options.populateHistory && entry.kind === "user") {
			this.editor.addToHistory?.(entry.text);
		}
	}

	for (const input of pendingDeferredInputs) {
		this.renderDeferredUserInput(input);
	}

	this.ui.requestRender();
};

InteractiveModeBase.prototype.renderSessionEntries = function (
	this: InteractiveModeBase,
	sessionEntries: SessionEntry[],
	options: {
		updateFooter?: boolean;
		populateHistory?: boolean;
		suppressCompactionBoundary?: VerbatimCompactionResult;
	} = {},
): void {
	this.resetTranscriptSelection();
	this.pendingTools.clear();
	const deferredInputs = [...this.deferredRenderedUserInputs];
	this.deferredRenderedUserInputs = [];
	this.deferredRenderedUserInputComponents.clear();
	if (options.updateFooter) {
		this.footer.invalidate();
		this.updateEditorBorderColor();
	}
	let messageBuffer: AgentMessage[] = [];
	const messageMeta = new Map<AgentMessage, PersistedMessageMeta>();
	let firstMessage = true;
	const flushMessages = (): void => {
		if (
			options.suppressCompactionBoundary &&
			firstMessage &&
			isSynthesizedCompactionBoundary(messageBuffer[0], options.suppressCompactionBoundary)
		) {
			messageBuffer = messageBuffer.slice(1);
		}
		firstMessage = false;
		for (const entry of chatEntriesFromAgentMessages(messageBuffer, messageMeta)) {
			const component = this.addRenderedChatEntry(entry);
			if (entry.kind === "assistant") addServiceTierWarnings(this, entry.message);
			if (entry.kind === "tool" && entry.isPartial !== false && component instanceof ToolExecutionComponent) {
				this.pendingTools.set(entry.toolCallId, component);
			}
			if (options.populateHistory && entry.kind === "user") this.editor.addToHistory?.(entry.text);
		}
		messageBuffer = [];
	};
	for (const entry of sessionEntries) {
		if (entry.type === "custom") {
			// An unrendered record between a tool call and its result must not split them into two cards.
			if (!this.session.extensionRunner.getEntryRenderer(entry.customType)) continue;
			flushMessages();
			this.addCustomEntryToChat(entry);
		} else {
			const messages = sessionEntryToContextMessages(entry);
			for (const message of messages) messageMeta.set(message, { id: entry.id, timestamp: entry.timestamp });
			messageBuffer.push(...messages);
			if ((entry.type === "compaction" || entry.type === "branch_summary") && entry.usage) {
				flushMessages();
				const suppressedLiveBoundary =
					entry.type === "compaction" &&
					options.suppressCompactionBoundary !== undefined &&
					entry.summary === options.suppressCompactionBoundary.compactedText &&
					entry.tokensBefore === options.suppressCompactionBoundary.tokensBefore;
				if (!suppressedLiveBoundary) this.addCompactionCostNotice(entry.type, entry.usage);
			}
		}
	}
	flushMessages();
	if (this.settingsManager.getShowCacheMissNotices()) {
		for (const miss of collectCacheMisses(
			sessionEntries,
			createCacheMissModelSource(this.session.modelRuntime),
		).values()) {
			const cause = describeCacheMissCause(miss);
			this.chatContainer.addChild(
				new Text(
					theme.fg(
						"warning",
						`Prompt cache miss${cause}: ${miss.missedTokens.toLocaleString()} tokens re-billed ($${miss.missedCost.toFixed(3)})`,
					),
					1,
					0,
				),
			);
		}
	}
	for (const input of deferredInputs) this.renderDeferredUserInput(input);
	this.ui.requestRender();
};

InteractiveModeBase.prototype.attachStartupNoticesContainer = function (
	this: InteractiveModeBase,
	options: { resetDetached?: boolean } = {},
): void {
	const disclosureIndex = this.chatContainer.children.indexOf(this.resourceDisclosureContainer);
	const noticesIndex = this.chatContainer.children.indexOf(this.startupNoticesContainer);
	if (disclosureIndex >= 0 && noticesIndex >= 0) return;
	if (disclosureIndex < 0) {
		if (options.resetDetached) this.resourceDisclosureContainer.clear();
		if (noticesIndex >= 0) this.chatContainer.children.splice(noticesIndex, 0, this.resourceDisclosureContainer);
		else this.chatContainer.addChild(this.resourceDisclosureContainer);
	}
	if (noticesIndex < 0) {
		if (options.resetDetached) this.startupNoticesContainer.clear();
		this.chatContainer.addChild(this.startupNoticesContainer);
	}
};

InteractiveModeBase.prototype.renderInitialMessages = function (this: InteractiveModeBase): void {
	this.attachStartupNoticesContainer({ resetDetached: true });
	const entries = buildContextEntries(this.sessionManager.getEntries(), this.sessionManager.getLeafId());
	this.renderSessionEntries(entries, { updateFooter: true, populateHistory: true });
	renderProjectTrustWarningIfNeeded(this);
	refreshInteractiveTasks(this);
};

function renderProjectTrustWarningIfNeeded(mode: InteractiveModeBase): void {
	if (mode.settingsManager.isProjectTrusted() || !hasTrustRequiringProjectResources(mode.sessionManager.getCwd())) {
		return;
	}
	if (mode.chatContainer.children.length > 0) {
		mode.chatContainer.addChild(new Spacer(1));
	}
	mode.chatContainer.addChild(
		new ThemedText(
			() =>
				theme.fg(
					"warning",
					`This project is not trusted. Project ${CONFIG_DIR_NAME} resources and packages are ignored. Use /trust to save a trust decision, then restart ${APP_NAME}.`,
				),
			1,
			0,
		),
	);
}

InteractiveModeBase.prototype.getUserInput = async function (
	this: InteractiveModeBase,
): Promise<InteractiveSubmission> {
	while (true) {
		const queuedInput = this.pendingUserInputs.shift();
		if (queuedInput !== undefined) {
			return queuedInput;
		}

		if (this.startupReplayActiveInput) {
			await this.drainStartupReplayCommands();
			continue;
		}

		return new Promise((resolve) => {
			this.onInputCallback = (submission: InteractiveSubmission) => {
				this.onInputCallback = undefined;
				resolve(submission);
			};
			if (!this.inputHandlerReadyRecorded) {
				this.inputHandlerReadyRecorded = true;
				recordTimeSinceReset("interactive-input-handler-ready");
				markLifecycleTiming("interactive-input-handler-ready");
				void (async () => {
					await yieldToEventLoop();
					// pi parity: the changelog/first-run notices need nothing from
					// extensions — render them right after the input handler is ready
					// instead of gating them behind the deferred extension reload (or,
					// when the user types immediately, behind the whole agent turn).
					this.showStartupNoticesIfNeeded(this.startupNoticesContainer);
					this.footerDataProvider.startGitWatcher();
					if (this.deferredStartupPending) {
						await this.ensureDeferredStartupComplete();
					}
				})().catch((error) => {
					const message = error instanceof Error ? error.message : String(error);
					console.error(`Deferred input-readiness startup task failed: ${message}`);
				});
			}
		});
	}
};

InteractiveModeBase.prototype.rebuildChatFromMessages = function (
	this: InteractiveModeBase,
	options: {
		suppressCompactionBoundary?: VerbatimCompactionResult;
		resetStartupDisclosure?: boolean;
	} = {},
): void {
	// Generic rebuilds keep the same session: pending fd/rg callbacks still own
	// this transcript. Full session replacements invalidate their generation in
	// renderCurrentSessionState() before rebuilding the replacement transcript.
	this.managedToolStatusStarted = false;
	this.lastStatusSpacer = undefined;
	this.lastStatusText = undefined;
	this.chatContainer.clear();
	if (options.resetStartupDisclosure) this.resourceDisclosureContainer.clear();
	this.attachStartupNoticesContainer();
	const entries = buildContextEntries(this.sessionManager.getEntries(), this.sessionManager.getLeafId());
	this.renderSessionEntries(entries, { suppressCompactionBoundary: options.suppressCompactionBoundary });
};

function isSynthesizedCompactionBoundary(message: AgentMessage | undefined, result: VerbatimCompactionResult): boolean {
	if (message?.role !== "custom" || message.customType !== "compaction" || !message.display) return false;
	const details = message.details as { strategy?: string } | undefined;
	if (details?.strategy !== "verbatim-lines") return false;
	const content = Array.isArray(message.content)
		? message.content
				.filter((block) => block.type === "text")
				.map((block) => block.text)
				.join("\n")
		: message.content;
	return content === VERBATIM_COMPACTION_PREFIX + result.compactedText;
}
